import { FormatPainterOutlined } from '@ant-design/icons';
import { json } from '@codemirror/lang-json';
import { Button, message, Space } from 'antd';
import React from 'react';
import CodeEditor from './index';

export interface JsonEditorProps {
  value: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
  height?: string;
  minHeight?: string;
  maxHeight?: string;
  placeholder?: string;
  toolbar?: boolean;
}

/** 解析 JSON 文本；空串返回 undefined；非法时抛错 */
export function parseJson(value: string): unknown | undefined {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return JSON.parse(trimmed);
}

/** 校验是否为合法 JSON（空串视为合法） */
export function isValidJson(value: string): boolean {
  try {
    parseJson(value);
    return true;
  } catch {
    return false;
  }
}

/** 格式化为 pretty print；非法 JSON 抛错 */
export function formatJson(value: string): string {
  if (!value.trim()) return '';
  return JSON.stringify(JSON.parse(value), null, 2);
}

/**
 * JSON 专用编辑器：语法高亮 + 一键格式化（pretty print）。
 * 基于通用 CodeEditor，后续如需其他格式（YAML 等）直接用 CodeEditor。
 */
const JsonEditor: React.FC<JsonEditorProps> = ({
  value,
  onChange,
  readOnly,
  height,
  minHeight,
  maxHeight,
  placeholder,
  toolbar = true,
}) => {
  const handleFormat = () => {
    if (!onChange) return;
    try {
      onChange(formatJson(value));
    } catch (e) {
      message.error(`JSON 格式错误：${e instanceof Error ? e.message : String(e)}`);
    }
  };

  return (
    <div>
      {toolbar && (
        <Space style={{ width: '100%', justifyContent: 'flex-end', marginBottom: 4 }} size={4}>
          <Button size="small" icon={<FormatPainterOutlined />} onClick={handleFormat}>
            格式化
          </Button>
        </Space>
      )}
      <CodeEditor
        value={value}
        onChange={onChange}
        extensions={[json()]}
        readOnly={readOnly}
        height={height}
        minHeight={minHeight}
        maxHeight={maxHeight}
        placeholder={placeholder}
      />
    </div>
  );
};

export default JsonEditor;